import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { mutate } from "swr";
import { Card, CardContent } from "@mui/material";
import axios from "axios";
import bcrypt from "bcryptjs";
import _ from "lodash";
import useGlobalContext from "../../hooks/useGlobalContext";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import localize from "../../common/localize";
import ActionButtons from "../../components/common/ActionButtons";
import ModalBackground from "../../components/common/ModalBackground";
import ErrorMessage from "../../components/common/ErrorMessage";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import { generateNoDataValues } from "../../common/utils";
import {
  getClearUserModel,
  getNameFilterOption,
  getUserData,
} from "../../common/EntitiesManagement";
import AddOrEditUser from "./components/AddOrEditUser";
import TrueFalseImage from "../../components/TrueFalseImage";
import ManageUserProfile from "./components/ManageUserProfile";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";

export default function Users() {
  const {
    userModel,
    setUserModel,
    usersData,
    AUTH_ENDPOINT,
    PROFILE_ENDPOINT,
    config,
    pageIndex,
    setPageIndex,
    toggleVisibleModalConfirm,
    updateEntity,
    setBasicFilterOptions,
  } = useGlobalContext();
  const { id } = useParams();
  const [usersDataFiltered, setUsersFiltered] = useState(usersData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [errorTitle, setErrorTitle] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [passwordConfirmed, setPasswordConfirmed] = useState(false);

  const [modifyFullName, setModifyFullName] = useState(false);
  const [modifyPassword, setModifyPassword] = useState(false);
  const [modifyPersonal, setModifyPersonal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getNameFilterOption());
  }, []);

  useEffect(() => {
    if (id) {
      setShowEditModal(true);
      const resolutionDataObj = usersData.Data.find(
        (userObj) => userObj.Id === Number(id)
      );
      setUserModel(resolutionDataObj);
    }
  }, [id]);

  useEffect(() => {
    setUsersFiltered(usersData.Data);
  }, [usersData]);

  const validateOperationResult = (result, isDelete) => {
    if (result.data.Message !== "Ok") {
      setErrorTitle(undefined);
      setErrorMessage(JSON.stringify(result.data.Message));
      setShowErrorModal(true);
      return;
    }

    mutate([AUTH_ENDPOINT, config]);
    if (isDelete) {
      if (pageIndex !== 0 && usersData.Data.length === 1) {
        setPageIndex(pageIndex - 1);
      }
    }
    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await axios.put(
      `${`${AUTH_ENDPOINT}/inactivate/${itemId}`}`,
      {},
      config
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const handleUpdateRequest = async (data) => {
    if (!modifyFullName && !modifyPassword && !modifyPersonal) {
      setErrorTitle(localize.app_name);
      setErrorMessage(localize.no_changes);
      setShowErrorModal(true);

      return;
    }

    const newData = _.omit(data, ["Personal", "UserRoles", "CreatedBy"]);

    if (modifyFullName) {
      const updateResult = await axios.put(
        `${`${AUTH_ENDPOINT}/updatefullname`}`,
        newData,
        config
      );
      validateOperationResult(updateResult, false);
    }

    if (modifyPassword) {
      const updateResult = await axios.put(
        `${`${AUTH_ENDPOINT}/updatepasswordasadmin`}`,
        {
          ...newData,
          UserPassword: bcrypt.hashSync(
            data.UserPassword,
            process.env.REACT_APP_BCRYPT_SALT
          ),
        },
        config
      );
      validateOperationResult(updateResult, false);
    }

    if (modifyPersonal) {
      const updateResult = await axios.put(
        `${`${AUTH_ENDPOINT}/updatepersonal`}`,
        newData,
        config
      );
      validateOperationResult(updateResult, false);
    }
  };

  const handleUserProfileChangeRequest = async (data) => {
    if (data?.UserRoles?.length > 0) {
      const result = await axios.put(
        `${`${PROFILE_ENDPOINT}/unassign/${data.UserRoles[0].Id}/${data.UserId}`}`,
        {},
        config
      );
      validateOperationResult(result, false);
    }
    if (data?.NewRole) {
      const updateResult = await axios.post(
        `${`${PROFILE_ENDPOINT}/assign/${data.NewRole.Id}/${data.UserId}`}`,
        {},
        config
      );
      validateOperationResult(updateResult, false);
    }
  };

  const handleCreateRequest = async (data) => {
    const newData = _.omit(data, ["Personal", "UserRoles", "CreatedBy"]);

    const updateResult = await axios.post(
      `${`${AUTH_ENDPOINT}/create`}`,

      {
        ...newData,
        ManagementId: getUserData().ManagementId,
        UserPassword: bcrypt.hashSync(
          data.UserPassword,
          process.env.REACT_APP_BCRYPT_SALT
        ),
      },
      config
    );

    validateOperationResult(updateResult, false);
  };

  const getUserRoleName = (roles) => {
    if (roles) {
      return roles[0].Name;
    }

    return undefined;
  };

  const disableSaveChanges = () =>
    (modifyPassword && !userModel.UserPassword) ||
    !userModel.FullName ||
    !userModel.UserName ||
    (modifyPassword && !passwordConfirmed);

    const disableSaveChangesForProfileAssignment = () =>
      !userModel?.NewRole && !userModel?.UnassignProfile;

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={errorTitle ?? localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showProfileModal && (
        <ModalBackground
        isDrawerMode
        hasRequiredValues
          showModal={showProfileModal}
          setShowModal={setShowProfileModal}
          title={localize.manage_user_profile}
          onClickToSubmit={async () => {
            await handleUserProfileChangeRequest(userModel);
            setShowProfileModal(false);
            setUserModel(getClearUserModel());
          }}
          onClickToCancel={() => {
            setShowProfileModal(false);
            setUserModel(getClearUserModel());
          }}
          Component={<ManageUserProfile />}
          validators={disableSaveChangesForProfileAssignment()}
        />
      )}

      {showEditModal && (
        <ModalBackground
        isDrawerMode
        hasRequiredValues
          validators={disableSaveChanges()}
          showModal={showEditModal}
          setShowModal={setShowEditModal}
          title={localize.edit_user}
          isEditForm
          onClickToSubmit={() => {
            handleUpdateRequest(userModel);
            setShowEditModal(false);
            setUserModel(getClearUserModel());
          }}
          onClickToCancel={() => {
            setShowEditModal(false);
            setUserModel(getClearUserModel());
          }}
          Component={
            <AddOrEditUser
              passwordConfirmed={passwordConfirmed}
              setPasswordConfirmed={setPasswordConfirmed}
              modifyFullName={modifyFullName}
              setModifyFullName={setModifyFullName}
              modifyPassword={modifyPassword}
              setModifyPassword={setModifyPassword}
              modifyPersonal={modifyPersonal}
              setModifyPersonal={setModifyPersonal}
            />
          }
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.users}
            subtitle={localize.users_description}
          />

          <SearcherAndAdd
            entityName="users_filter"
            handleAddBtn={() => {
              setModifyFullName(true);
              setModifyPassword(true);
              setModifyPersonal(true);
              setShowAddModal(true);
            }}
            modal={
              <ModalBackground
              isDrawerMode
              hasRequiredValues
                validators={disableSaveChanges()}
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_user}
                onClickToSubmit={() => {
                  setPasswordConfirmed(false);
                  handleCreateRequest(userModel);
                  setShowAddModal(false);
                  setUserModel(getClearUserModel());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setUserModel(getClearUserModel());
                }}
                Component={
                  <AddOrEditUser
                    passwordConfirmed={passwordConfirmed}
                    setPasswordConfirmed={setPasswordConfirmed}
                    modifyFullName={modifyFullName}
                    setModifyFullName={setModifyFullName}
                    modifyPassword={modifyPassword}
                    setModifyPassword={setModifyPassword}
                    modifyPersonal={modifyPersonal}
                    setModifyPersonal={setModifyPersonal}
                  />
                }
              />
            }
          />
        </CardContent>
      </Card>

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={userModel.UserName}
          entityType={localize.users}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setUserModel(getClearUserModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(userModel.UserId);
            setShowDeleteModal(false);
            setUserModel(getClearUserModel());
          }}
        />
      )}

      <TableComponent
        firstColumnTitle={localize.actions}
        columnTitles={[
          localize.username,
          localize.full_name,
          localize.profile,
          localize.allowed_on_mobile,
        ]}
        results={
          usersDataFiltered?.length > 0
            ? usersDataFiltered?.map((item) => ({
                key: item.UserId,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setModifyFullName(false);
                        setModifyPassword(false);
                        setModifyPersonal(false);
                        setPasswordConfirmed(true);
                        setUserModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setUserModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                      onClickForDetailButton={() => {
                        setShowProfileModal(true);
                        setUserModel(item);
                      }}
                      detailsButtonText={localize.manage_user_profile}
                    />
                  </div>,
                  item.UserName,
                  item.FullName,
                  <ValueOrNoDataLabel data={getUserRoleName(item.UserRoles)} />,
                  item.PersonalId > 0 ? (
                    <TrueFalseImage value />
                  ) : (
                    <TrueFalseImage value={false} />
                  ),
                ],
              }))
            : [generateNoDataValues(5)]
        }
      />
    </div>
  );
}
